import { Routes, Route } from 'react-router-dom';
import { AuthProvider, ProtectedRoute, AdminRoute } from '@features/auth';
import { MainLayout, SectionLayout } from '@components/layout';
import { LoginPage } from '@pages/LoginPage';
import { WorkspacesPage } from '@pages/WorkspacesPage';
import { WorkspacePage } from '@pages/WorkspacePage';
import { RunsPage } from '@pages/RunsPage';
import { RunDetailPage } from '@pages/RunDetailPage';
import { ProjectPage } from '@pages/ProjectPage';
import { AiUsageTab } from '@pages/dashboards/AiUsageTab';
import { ProfileTab } from '@pages/settings/ProfileTab';
import { GitCredentialsTab } from '@pages/settings/GitCredentialsTab';
import { FeaturePreferencesTab } from '@pages/settings/FeaturePreferencesTab';
import { PacksTab } from '@pages/admin/provisioning/PacksTab';
import { ProjectsTab } from '@pages/admin/provisioning/ProjectsTab';
import { FeaturesTab } from '@pages/admin/registry/FeaturesTab';
import { ImagesTab } from '@pages/admin/registry/ImagesTab';
import { AiModelsTab } from '@pages/admin/registry/AiModelsTab';
import { LoginProvidersTab } from '@pages/admin/system/LoginProvidersTab';
import { GitServerManager } from '@features/admin/components/GitServerManager';
import { LoginCallbackPage } from '@features/logins/components/LoginCallbackPage';
import { ROUTES } from '@utils/constants';
import { legacyRedirectRoutes } from '@utils/redirects';

function AppRoutes() {
  return (
    <Routes>
      <Route path={ROUTES.LOGIN} element={<LoginPage />} />
      <Route
        path={ROUTES.HOME}
        element={
          <ProtectedRoute>
            <MainLayout>
              <WorkspacesPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={`${ROUTES.WORKSPACE}/:id`}
        element={
          <ProtectedRoute>
            <MainLayout>
              <WorkspacePage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={`${ROUTES.PROJECT}/:projectId`}
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProjectPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.RUNS}
        element={
          <ProtectedRoute>
            <MainLayout>
              <SectionLayout title="Runs" sectionPath={ROUTES.RUNS} />
            </MainLayout>
          </ProtectedRoute>
        }
      >
        <Route index element={<RunsPage />} />
        <Route path=":id" element={<RunDetailPage />} />
      </Route>
      <Route
        path={ROUTES.DASHBOARDS}
        element={
          <ProtectedRoute>
            <MainLayout>
              <SectionLayout
                title="Dashboards"
                sectionPath={ROUTES.DASHBOARDS}
                subtitle="Usage and activity across the platform."
              />
            </MainLayout>
          </ProtectedRoute>
        }
      >
        <Route index element={<AiUsageTab />} />
      </Route>
      <Route
        path={ROUTES.LOGIN_CALLBACK}
        element={
          <ProtectedRoute>
            <LoginCallbackPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.SETTINGS}
        element={
          <ProtectedRoute>
            <MainLayout>
              <SectionLayout title="Settings" sectionPath={ROUTES.SETTINGS} />
            </MainLayout>
          </ProtectedRoute>
        }
      >
        <Route index element={<GitCredentialsTab />} />
        <Route path="features" element={<FeaturePreferencesTab />} />
        <Route path="profile" element={<ProfileTab />} />
      </Route>

      <Route
        path={ROUTES.ADMIN_PROVISIONING}
        element={
          <AdminRoute>
            <MainLayout>
              <SectionLayout
                title="Provisioning"
                sectionPath={ROUTES.ADMIN_PROVISIONING}
                subtitle="Compose packs of devcontainer features, and assign them to projects."
              />
            </MainLayout>
          </AdminRoute>
        }
      >
        <Route index element={<PacksTab />} />
        <Route path="projects" element={<ProjectsTab />} />
      </Route>

      <Route
        path={ROUTES.ADMIN_REGISTRY}
        element={
          <AdminRoute>
            <MainLayout>
              <SectionLayout
                title="Registry"
                sectionPath={ROUTES.ADMIN_REGISTRY}
                subtitle="The devcontainer feature registry and the images built from it."
              />
            </MainLayout>
          </AdminRoute>
        }
      >
        <Route index element={<FeaturesTab />} />
        <Route path="images" element={<ImagesTab />} />
        <Route path="ai-models" element={<AiModelsTab />} />
      </Route>

      <Route
        path={ROUTES.ADMIN_SYSTEM}
        element={
          <AdminRoute>
            <MainLayout>
              <SectionLayout
                title="System"
                sectionPath={ROUTES.ADMIN_SYSTEM}
                subtitle="Per-installation plumbing: where code lives and who may log in."
              />
            </MainLayout>
          </AdminRoute>
        }
      >
        <Route index element={<GitServerManager />} />
        <Route path="logins" element={<LoginProvidersTab />} />
      </Route>

      {legacyRedirectRoutes()}
    </Routes>
  );
}

export function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}
